import React, { useEffect } from 'react';
import { View } from 'react-native';
import { styled, SxProp } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import {
  EdgeInsets,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';

import {
  BaseText,
  Button,
  ButtonStyleEnum,
  Separator,
  ScrollView,
  LogoImage,
} from '@components';
import { IS_MOBILE, IS_WEB, parseApiError } from '@utils';
import {
  useAsyncInit,
  useNavOptionsSet,
  usePersonalizationNavigation,
} from '@hooks';
import { ToastService } from '@services';
import { QuestTopics } from './components/QuestTopics';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { useQuestionnaireModel } from '@models/Questionnaire/QuestionnaireModel';

const Container = styled(ScrollView)({
  flex: 1,
  backgroundColor: '$background0',
});

function contentContainerStyle(props: EdgeInsets): SxProp {
  return (theme) => ({
    flexGrow: 1,
    px: ['$6', null],
    pt: theme.space.$8 + props.top,
    pb: theme.space.$6 + props.bottom,
    alignItems: 'center',
    justifyContent: [null, 'center'],
  });
}

const LogoContainer = styled(View)({
  alignItems: 'center',
});

const ContentContainer = styled(View)({
  width: [null, 356],
});

const Title = styled(BaseText)({
  color: '$heading900',
  fontWeight: 'semi',
  textAlign: 'center',
  variant: IS_WEB ? 'text.xbig' : 'text.xxlarge',
});

export const QuestionnaireScreen = observer(() => {
  const { t } = useTranslation();
  const questionnaire = useQuestionnaireModel();
  const viewer = useViewerModel();
  const insets = useSafeAreaInsets();
  const { navigateToNextScreen, navigateToHome } =
    usePersonalizationNavigation();

  useNavOptionsSet({ title: t('questionnaire.title') });

  useEffect(() => {
    // On web user can reach this page by entering route in address bar
    // We check if need to ask a user quest if no need we will redirect him to
    // home page
    if (IS_WEB && !viewer.currentUser?.settings.isNeedToAskQuest) {
      navigateToHome();
    }
  }, []);

  useAsyncInit(async () => {
    try {
      await questionnaire.list.fetch.run();
    } catch (e) {
      ToastService.showError(parseApiError(e).message);
    }
  });

  async function save() {
    try {
      await questionnaire.save.run();

      navigateToNextScreen();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  async function skipForNow() {
    try {
      await questionnaire.skipAndSave.run();

      navigateToNextScreen();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  async function skipAndNeverAsk() {
    try {
      await questionnaire.skipAndNeverAsk.run();

      navigateToNextScreen();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  const isDisabledSaveButton =
    questionnaire.skipAndNeverAsk.inProgress ||
    questionnaire.skipAndSave.inProgress ||
    !questionnaire.canBeSaved;

  const isDisabledSkipButton =
    questionnaire.skipAndNeverAsk.inProgress ||
    questionnaire.save.inProgress;

  const isDisabledNewerAskButton =
    questionnaire.skipAndSave.inProgress ||
    questionnaire.save.inProgress;

  return (
    <Container contentContainerSx={contentContainerStyle(insets)}>
      <ContentContainer>
        {IS_MOBILE && (
          <LogoContainer>
            <LogoImage />
            <Separator size="$8" />
          </LogoContainer>
        )}
        <Title>{`${t('questionnaire.title')} 🎧`}</Title>

        <Separator size="$7" />

        <QuestTopics list={questionnaire.list} />

        <View>
          <Separator size="$5" />
          <Button
            onPress={save}
            isLoading={questionnaire.save.inProgress}
            disabled={isDisabledSaveButton}
          >
            {t('questionnaire.save')}
          </Button>

          <Separator size="$5" />
          <Button
            buttonStyle={ButtonStyleEnum.Outline}
            onPress={skipForNow}
            isLoading={questionnaire.skipAndSave.inProgress}
            disabled={isDisabledSkipButton}
          >
            {t('questionnaire.skip')}
          </Button>

          <Separator size="$5" />
          <Button
            buttonStyle={ButtonStyleEnum.Ghost}
            onPress={skipAndNeverAsk}
            isLoading={questionnaire.skipAndNeverAsk.inProgress}
            disabled={isDisabledNewerAskButton}
          >
            {t('questionnaire.skipNeverAsk')}
          </Button>
        </View>
      </ContentContainer>
    </Container>
  );
});
